import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../lib/api-client";
import { queryKeys } from "../query-keys";
import {
  InterviewSchema,
  SelectSlotAckSchema,
  InterviewUpdateAckSchema,
  type CreateInterviewRequest,
  type SelectInterviewSlotRequest,
  type UpdateInterviewRequest,
} from "@applicant-reviewer/api";

// ---------------------------------------------------------------------------
// Create interview
// ---------------------------------------------------------------------------
export function useCreateInterview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateInterviewRequest) =>
      apiClient.post("/interviews", InterviewSchema, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.interviews.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.pipeline.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// Select interview slot
// ---------------------------------------------------------------------------
interface SelectInterviewSlotVariables {
  interviewId: string;
  payload: SelectInterviewSlotRequest;
}

export function useSelectInterviewSlot() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ interviewId, payload }: SelectInterviewSlotVariables) =>
      apiClient.put(`/interviews/${interviewId}/slot`, SelectSlotAckSchema, payload),
    onSuccess: (_data, { interviewId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.interviews.detail(interviewId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.interviews.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// Update interview (reschedule, cancel, add feedback notes)
// ---------------------------------------------------------------------------
interface UpdateInterviewVariables {
  interviewId: string;
  payload: UpdateInterviewRequest;
}

export function useUpdateInterview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ interviewId, payload }: UpdateInterviewVariables) =>
      apiClient.put(`/interviews/${interviewId}`, InterviewUpdateAckSchema, payload),
    onSuccess: (_data, { interviewId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.interviews.detail(interviewId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.interviews.lists() });
    },
  });
}
